import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArchiveRestore, DatabaseBackup, Download, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { serverApi } from '../services/api';
import type { ServerBackup } from '../types/server';
import { formatBytes } from '../utils/resourceFormatting';
import { getApiErrorDetails } from '../utils/apiError';

interface BackupsPanelProps {
  serverId: string;
}

export function BackupsPanel({ serverId }: BackupsPanelProps) {
  const { t } = useTranslation();
  const [backups, setBackups] = useState<ServerBackup[]>([]);
  const [retentionCount, setRetentionCount] = useState(3);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const active = useMemo(
    () => backups.some(backup => (
      backup.status === 'pending'
      || backup.status === 'deleting'
      || backup.latestRestore?.status === 'pending'
      || backup.latestRestore?.status === 'in_progress'
    )),
    [backups],
  );

  const load = useCallback(async (quiet = false) => {
    try {
      const response = await serverApi.getBackups(serverId);
      setBackups(response.backups);
      setRetentionCount(response.retentionCount);
      setError('');
    } catch (requestError) {
      if (!quiet) setError(getApiErrorDetails(requestError, t('backups.loadFailed')).message);
    } finally {
      setLoading(false);
    }
  }, [serverId, t]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(true), 5_000);
    return () => clearInterval(timer);
  }, [load]);

  const create = async () => {
    setCreating(true);
    setError('');
    try {
      await serverApi.createBackup(serverId);
      await load();
    } catch (requestError) {
      setError(getApiErrorDetails(requestError, t('backups.createFailed')).message);
    } finally {
      setCreating(false);
    }
  };

  const openRestore = (backupId: string) => {
    setRestoreId(current => current === backupId ? null : backupId);
    setError('');
  };

  const restore = async () => {
    if (!restoreId) return;
    setRestoring(true);
    setError('');
    try {
      await serverApi.restoreBackup(serverId, restoreId);
      setRestoreId(null);
      await load();
    } catch (requestError) {
      setError(getApiErrorDetails(requestError, t('backups.restoreFailed')).message);
    } finally {
      setRestoring(false);
    }
  };

  const download = async (backupId: string) => {
    setDownloadingId(backupId);
    setError('');
    try {
      const response = await serverApi.getBackupDownload(serverId, backupId);
      window.location.assign(response.download.url);
    } catch (requestError) {
      setError(getApiErrorDetails(requestError, t('backups.downloadFailed')).message);
    } finally {
      setDownloadingId(null);
    }
  };

  const restoreStatusLabel = (status: NonNullable<ServerBackup['latestRestore']>['status']) => {
    if (status === 'pending') return t('backups.restorePending');
    if (status === 'in_progress') return t('backups.restoreInProgress');
    if (status === 'succeeded') return t('backups.restoreSucceeded');
    return t('backups.restoreFailedStatus');
  };

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="section-title flex items-center gap-2">
          <DatabaseBackup className="w-5 h-5 text-gray-400" />
          {t('backups.title')}
        </h3>
        <button onClick={() => void load()} className="text-gray-500 hover:text-gray-200" title={t('common.refresh')}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        {t('backups.retention', { count: retentionCount })}
      </p>
      {error && <div className="mb-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded p-2">{error}</div>}

      <button
        onClick={create}
        disabled={creating || active || restoreId !== null}
        className="btn-secondary w-full mb-4"
      >
        {creating ? <div className="spinner w-4 h-4" /> : <DatabaseBackup className="w-4 h-4" />}
        {active ? t('backups.operationActive') : t('backups.create')}
      </button>

      <div className="space-y-3">
        {!loading && backups.length === 0 && <p className="text-sm text-gray-500">{t('backups.empty')}</p>}
        {backups.map(backup => (
          <div key={backup.id} className="rounded-lg border border-gray-800 bg-gray-950/40 p-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-200">{new Date(backup.createdAt).toLocaleString()}</span>
              <span className={`capitalize ${backup.status === 'failed' ? 'text-red-400' : backup.status === 'available' ? 'text-neon-400' : 'text-blue-300'}`}>
                {backup.phase.replace(/_/g, ' ')}
              </span>
            </div>
            {backup.sizeBytes !== null && (
              <div className="text-gray-500 mt-1">{formatBytes(backup.sizeBytes)} · {backup.format}</div>
            )}
            {backup.lastError && <div className="text-red-300 mt-2 break-words">{backup.lastError}</div>}

            {backup.latestRestore && (
              <div className="mt-3 border-t border-gray-800 pt-3 space-y-1.5 text-gray-400">
                <div className="flex items-center justify-between gap-2">
                  <span>{t('backups.latestRestore')}</span>
                  <span className={
                    backup.latestRestore.status === 'succeeded'
                      ? 'text-neon-400'
                      : backup.latestRestore.status === 'failed'
                        ? 'text-red-400'
                        : 'text-blue-300'
                  }>
                    {restoreStatusLabel(backup.latestRestore.status)}
                  </span>
                </div>
                <div>{t('backups.restorePhase', { phase: backup.latestRestore.phase.replace(/_/g, ' ') })}</div>
                <div>{t('backups.restoreAttempts', { count: backup.latestRestore.attempts })}</div>
                {backup.latestRestore.nextRetryAt && (
                  <div>{t('backups.restoreRetryAt', { time: new Date(backup.latestRestore.nextRetryAt).toLocaleString() })}</div>
                )}
                {backup.latestRestore.lastError && (
                  <div className="text-red-300 break-words">{backup.latestRestore.lastError}</div>
                )}
              </div>
            )}

            {backup.status === 'available' && (
              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  onClick={() => openRestore(backup.id)}
                  disabled={active || restoring || creating}
                  className="text-neon-400 hover:text-neon-300 disabled:text-gray-600 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ArchiveRestore className="w-3.5 h-3.5" /> {t('backups.restore')}
                </button>
                <button
                  onClick={() => void download(backup.id)}
                  disabled={downloadingId !== null}
                  className="text-blue-300 hover:text-blue-200 disabled:text-gray-600 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {downloadingId === backup.id
                    ? <div className="spinner w-3.5 h-3.5" />
                    : <Download className="w-3.5 h-3.5" />}
                  {t('backups.download')}
                </button>
              </div>
            )}

            {restoreId === backup.id && (
              <div className="mt-3 space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                <div className="flex items-start gap-2 text-amber-200">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{t('backups.restoreConfirmTitle')}</div>
                    <p className="mt-1 leading-relaxed text-amber-200/80">{t('backups.restoreConfirmWarning')}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void restore()}
                    disabled={restoring || active || creating}
                    className="btn-danger flex-1 text-xs"
                  >
                    {restoring ? <div className="spinner w-3.5 h-3.5" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                    {t('backups.restoreConfirm')}
                  </button>
                  <button
                    onClick={() => setRestoreId(null)}
                    disabled={restoring}
                    className="btn-secondary text-xs"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
